import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, Table, History, Wifi, WifiOff } from 'lucide-react';
import { webSocketService } from '../services/webSocketService';

// Import the reservation components
import ReservationsList from './Reservations/ReservationsList';
import TableManagement from './Reservations/TableManagement';
import ReservationHistory from './Reservations/ReservationHistory';

type TabType = 'reservations' | 'tables' | 'history';

const Reservations: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('reservations');
  const [wsConnected, setWsConnected] = useState(false);

  // Monitor WebSocket connection status
  useEffect(() => {
    const componentId = 'Reservations';
    let statusTimeout: NodeJS.Timeout | null = null;
    let mounted = true;

    // Get initial connection status
    const initialStatus = webSocketService.getConnectionStatus() === 'connected';
    if (mounted) setWsConnected(initialStatus);

    // Register this component
    webSocketService.registerComponent(componentId);

    // Listen to connection status with debouncing
    const connectionSub = webSocketService.connectionStatus().subscribe({
      next: (connected: boolean) => {
        if (!mounted) return;

        if (statusTimeout) clearTimeout(statusTimeout);
        statusTimeout = setTimeout(() => {
          if (mounted) {
            setWsConnected(connected);
          }
        }, 100);
      },
      error: (error: unknown) => {
        if (!mounted) return;
        setWsConnected(false);
      }
    });

    return () => {
      mounted = false;
      if (statusTimeout) clearTimeout(statusTimeout);
      webSocketService.unregisterComponent(componentId);
      connectionSub.unsubscribe();
    };
  }, []);

  const tabs = [
    {
      id: 'reservations' as TabType,
      label: 'Current Reservations',
      icon: Calendar,
      component: ReservationsList
    },
    {
      id: 'tables' as TabType,
      label: 'Table Management',
      icon: Table,
      component: TableManagement
    },
    {
      id: 'history' as TabType,
      label: 'Reservation History',
      icon: History,
      component: ReservationHistory
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || ReservationsList;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          Reservations
          <div title={wsConnected ? "Live updates connected" : "Live updates disconnected"}>
            {wsConnected ? (
              <Wifi className="w-6 h-6 text-green-500" />
            ) : (
              <WifiOff className="w-6 h-6 text-red-500" />
            )}
          </div>
        </h1>
        <p className="text-muted-foreground">Manage reservations, tables, and restaurant layout</p>
      </div>

      {/* Tabs */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <Button
                  key={tab.id}
                  variant={isActive ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 ${
                    isActive 
                      ? 'text-primary-foreground border-r-2 border-blue-600 text-blue-600 !bg-blue-100 outline-blue-600' 
                      : 'border-r-2 border-blue-600 text-muted-foreground hover:text-foreground hover:bg-blue-100'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </Button>
              );
            })}
          </div>
        </CardHeader>
        <CardContent>
          <div className="mt-4">
            <ActiveComponent />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reservations;
